// ============================================================================
// QUICK CREATE AVATAR MODAL
// Two modes: 'manual' (name + description → AI generates prompt → create)
//            'from-persona' (pre-filled from persona → single button create)
// ============================================================================

import React from 'react';
import { X, Loader2, Sparkles, Bot, ChevronDown, ChevronUp } from 'lucide-react';
import { AvatarDefinition } from '../types';
import { useAvatarCreation, AvatarCreationPersona } from '../hooks/useAvatarCreation';

export interface QuickCreateAvatarModalProps {
  mode: 'manual' | 'from-persona';
  projectId: string;
  sessionId?: string;
  persona?: AvatarCreationPersona;
  onClose: () => void;
  onCreated: (avatar: AvatarDefinition) => void;
}

interface ManualCreateBodyProps {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  saving: boolean;
  generating: boolean;
  handleGeneratePrompt: () => void;
  systemPrompt: string;
  keywords: string[];
  showPromptPreview: boolean;
  setShowPromptPreview: (updater: (prev: boolean) => boolean) => void;
}

const ManualCreateBody: React.FC<ManualCreateBodyProps> = ({
  name,
  setName,
  description,
  setDescription,
  saving,
  generating,
  handleGeneratePrompt,
  systemPrompt,
  keywords,
  showPromptPreview,
  setShowPromptPreview
}) => {
  const generateDisabled = generating || saving || !name.trim() || !description.trim();

  return (
    <>
      {/* Name */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Avatar Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving || generating}
          placeholder="e.g. Sarah the Procurement Lead"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#0f172a',
            background: 'white',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Description / Role */}
      <div>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Role / Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={saving || generating}
          placeholder="Describe the role and perspective this avatar represents..."
          rows={3}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#0f172a',
            background: 'white',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Generate prompt button + preview */}
      <button
        onClick={handleGeneratePrompt}
        disabled={generateDisabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px 16px',
          background: generateDisabled ? '#e2e8f0' : '#f0fdfa',
          color: generateDisabled ? '#94a3b8' : '#00c7c4',
          border: '1px solid #99f6e4',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: (generating || saving) ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s'
        }}
      >
        {generating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
        {generating ? 'Generating AI Prompt...' : 'Generate AI Prompt'}
      </button>

      {systemPrompt && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
          <button
            onClick={() => setShowPromptPreview(p => !p)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#f8fafc',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              color: '#374151'
            }}
          >
            <span>System Prompt Preview</span>
            {showPromptPreview ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showPromptPreview && (
            <div style={{ padding: '12px 14px', fontSize: '13px', color: '#475569', lineHeight: '1.6', borderTop: '1px solid #e2e8f0', maxHeight: '150px', overflowY: 'auto', background: 'white' }}>
              {systemPrompt}
            </div>
          )}
        </div>
      )}

      {keywords.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {keywords.map(kw => (
            <span key={kw} style={{ padding: '2px 10px', background: '#eff6ff', color: '#3b82f6', borderRadius: '999px', fontSize: '12px', fontWeight: '500' }}>
              {kw}
            </span>
          ))}
        </div>
      )}
    </>
  );
};

interface FromPersonaBodyProps {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
}

const FromPersonaBody: React.FC<FromPersonaBodyProps> = ({ name, setName, description, setDescription }) => (
  <>
    {/* Name */}
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
        Avatar Name
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled
        placeholder="e.g. Sarah the Procurement Lead"
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#0f172a',
          background: '#f8fafc',
          boxSizing: 'border-box'
        }}
      />
    </div>

    {/* Description / Role */}
    <div>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
        Role / Description
      </label>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled
        placeholder="Describe the role and perspective this avatar represents..."
        rows={3}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#0f172a',
          background: '#f8fafc',
          resize: 'vertical',
          boxSizing: 'border-box'
        }}
      />
    </div>

    {/* From-persona info */}
    <div style={{ background: '#f0fdfa', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#0d9488' }}>
      AI will generate a system prompt based on this persona's goals and pain points.
    </div>
  </>
);

export const QuickCreateAvatarModal: React.FC<QuickCreateAvatarModalProps> = ({
  mode,
  projectId,
  sessionId,
  persona,
  onClose,
  onCreated
}) => {
  const {
    name,
    setName,
    description,
    setDescription,
    generating,
    systemPrompt,
    keywords,
    saving,
    error,
    showPromptPreview,
    setShowPromptPreview,
    handleGeneratePrompt,
    handleCreateManual,
    handleCreateFromPersona
  } = useAvatarCreation(mode, projectId, sessionId, persona, onCreated);

  const isFromPersona = mode === 'from-persona';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          maxWidth: '540px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#e0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={20} color="#00c7c4" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                {isFromPersona ? `Create Avatar from Persona` : 'Create Project Avatar'}
              </h2>
              {isFromPersona && persona && (
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{persona.name} — {persona.role}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
          >
            <X size={20} color="#94a3b8" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {isFromPersona ? (
            <FromPersonaBody name={name} setName={setName} description={description} setDescription={setDescription} />
          ) : (
            <ManualCreateBody
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              saving={saving}
              generating={generating}
              handleGeneratePrompt={handleGeneratePrompt}
              systemPrompt={systemPrompt}
              keywords={keywords}
              showPromptPreview={showPromptPreview}
              setShowPromptPreview={setShowPromptPreview}
            />
          )}

          {/* Error */}
          {error && (
            <p style={{ margin: 0, fontSize: '13px', color: '#ef4444', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#f8fafc' }}>
          <button
            onClick={onClose}
            disabled={saving || generating}
            style={{
              padding: '9px 16px',
              background: 'white',
              color: '#374151',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          {isFromPersona ? (
            <button
              onClick={handleCreateFromPersona}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                background: saving ? '#94a3b8' : '#00c7c4',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Bot size={16} />}
              {saving ? 'Creating...' : 'Create Avatar'}
            </button>
          ) : (
            <button
              onClick={handleCreateManual}
              disabled={saving || !systemPrompt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                background: (saving || !systemPrompt) ? '#94a3b8' : '#00c7c4',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (saving || !systemPrompt) ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Bot size={16} />}
              {saving ? 'Creating...' : 'Create & Activate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickCreateAvatarModal;
